import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadCurrentUser, setCredentials } from "../../store/authSlice";
import { signInWithCredentials } from "../utils/authApi";
import { NAV } from "../Navbar/navbarTheme";
import AuthFormLayout from "./AuthFormLayout";
import {
  authAlertSx,
  authFieldSx,
  authFooterTextSx,
  authLinkSx,
  authSubmitButtonSx,
} from "./authStyles";

const SignInPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (location.state?.registeredEmail) {
      setEmail(location.state.registeredEmail);
      setSuccessMessage(
        "Account created successfully. Please sign in with your new details.",
      );
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await signInWithCredentials({
        email: trimmedEmail,
        password,
      });

      dispatch(setCredentials({ token }));
      await dispatch(loadCurrentUser()).unwrap();

      const redirectTo =
        typeof location.state?.from === "string" && location.state.from
          ? location.state.from
          : "/Profile";
      navigate(redirectTo, { replace: true });
    } catch (submitError) {
      setError(
        submitError?.message ||
          "Unable to sign in right now. Please try again later.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthFormLayout
      title="Sign in"
      subtitle="Access your trade account to manage quotes and orders."
      icon={LoginOutlinedIcon}
      footer={
        <Typography sx={authFooterTextSx}>
          Don&apos;t have an account?{" "}
          <Typography
            component={Link}
            to="/Register"
            sx={{ ...authLinkSx, display: "inline" }}
          >
            Register
          </Typography>
        </Typography>
      }
    >
      {successMessage ? (
        <Alert severity="success" sx={authAlertSx}>
          {successMessage}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={authAlertSx}>
          {error}
        </Alert>
      ) : null}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <TextField
            fullWidth
            required
            label="Email address"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            sx={authFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon
                    sx={{ color: NAV.inkMuted, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            required
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            sx={authFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon
                    sx={{ color: NAV.inkMuted, fontSize: 20 }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    type="button"
                    size="small"
                    onClick={() => setShowPassword((open) => !open)}
                    sx={{
                      minWidth: "auto",
                      px: 0.75,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: NAV.inkMuted,
                    }}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </Button>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isSubmitting}
            sx={authSubmitButtonSx}
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </Button>
        </Stack>
      </Box>
    </AuthFormLayout>
  );
};

export default SignInPage;
